import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Button } from './ui/button';
import EMatrixLogo from './EMatrixLogo';
import DataUploadDialog from './DataUploadDialog';
import HelperTeamsConfigDialog from './HelperTeamsConfigDialog';
import { 
  LayoutDashboard, 
  Users, 
  LogOut, 
  Menu, 
  X,
  ChevronLeft,
  Activity,
  Settings,
  FolderOpen,
  Database,
  Trash2,
  Upload,
  UserX
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Avatar, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { dataStatus, removeData, isLoading: dataLoading } = useData();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [helperTeamsDialogOpen, setHelperTeamsDialogOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRemoveData = async () => {
    const result = await removeData();
    if (result.success) {
      toast.success('Datenstand entfernt');
      setDeleteDialogOpen(false);
      // Navigate to dashboard to reset any open reports/services
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Fehler beim Entfernen der Daten');
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { path: '/admin', icon: Users, label: 'Benutzerverwaltung', show: user?.is_admin },
    { path: '/admin/services', icon: Settings, label: 'Services', show: user?.is_admin },
    { path: '/admin/categories', icon: FolderOpen, label: 'Kategorien', show: user?.is_admin },
    { path: '/admin/activity', icon: Activity, label: 'Aktivitätsprotokoll', show: user?.is_admin },
  ];

  return (
    <div className="flex h-screen" style={{ background: '#E7F0FA' }}>
      {/* Sidebar - Desktop */}
      <aside 
        className={`hidden md:flex flex-col sidebar transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b" style={{ borderColor: '#C8D8E8' }}>
          {sidebarOpen ? (
            <EMatrixLogo size="small" />
          ) : (
            <EMatrixLogo size="small" showText={false} />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hover:bg-[#E7F0FA]"
            style={{ color: '#5A7A9B' }}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={`h-5 w-5 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.filter(item => item.show).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''} ${!sidebarOpen ? 'justify-center px-2' : ''}`
              }
              data-testid={`nav-${item.path.replace(/\//g, '-').substring(1)}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Exclusion List Link */}
        <div className="px-4 py-2 border-t" style={{ borderColor: '#C8D8E8' }}>
          {sidebarOpen ? (
            <NavLink
              to="/exclusion-list"
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
              data-testid="nav-exclusion-list"
            >
              <UserX className="h-5 w-5 flex-shrink-0" />
              <span>Ausschlussliste</span>
            </NavLink>
          ) : (
            <NavLink
              to="/exclusion-list"
              className={({ isActive }) =>
                `sidebar-link justify-center px-2 ${isActive ? 'active' : ''}`
              }
              data-testid="nav-exclusion-list-collapsed"
              title="Ausschlussliste"
            >
              <UserX className="h-5 w-5" />
            </NavLink>
          )}
        </div>

        {/* Data Status Section */}
        <div 
          className={`p-4 border-t ${dataStatus?.loaded ? 'bg-green-50' : ''}`} 
          style={{ borderColor: dataStatus?.loaded ? '#22c55e' : '#C8D8E8', borderTopWidth: dataStatus?.loaded ? '2px' : '1px' }}
        >
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Database className={`h-4 w-4 ${dataStatus?.loaded ? 'text-green-600' : ''}`} style={{ color: dataStatus?.loaded ? '#22c55e' : '#5A7A9B' }} />
                <span className={`text-xs font-medium ${dataStatus?.loaded ? 'text-green-700' : ''}`} style={{ color: dataStatus?.loaded ? '#15803d' : '#5A7A9B' }}>Datenstand</span>
              </div>
              {dataStatus?.loaded ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium truncate" style={{ color: '#0D2440' }}>
                    {dataStatus.system_name || 'Unbenannt'}
                  </div>
                  <div className="text-xs" style={{ color: '#5A7A9B' }}>
                    {dataStatus.accounts_count?.toLocaleString()} Accounts
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => setUploadDialogOpen(true)}
                      data-testid="update-data-button"
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Neu
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteDialogOpen(true)}
                      data-testid="delete-data-button"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => setHelperTeamsDialogOpen(true)}
                    data-testid="helper-teams-config-button"
                    style={{ borderColor: '#f59e0b', color: '#b45309' }}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Hilfsteams definieren
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => setUploadDialogOpen(true)}
                  data-testid="load-data-button"
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Datenstand laden
                </Button>
              )}
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setUploadDialogOpen(true)}
              className={`w-full hover:bg-[#E7F0FA] ${dataStatus?.loaded ? 'text-green-600' : ''}`}
              style={{ color: dataStatus?.loaded ? '#22c55e' : '#5A7A9B' }}
              data-testid="load-data-button-collapsed"
              title={dataStatus?.loaded ? `Datenstand: ${dataStatus.system_name}` : 'Datenstand laden'}
            >
              <Database className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* User section */}
        <div className="p-4 border-t" style={{ borderColor: '#C8D8E8' }}>
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback style={{ background: '#E7F0FA', color: '#2E5E99' }}>
                  {getInitials(user?.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: '#0D2440' }}>{user?.name}</p>
                <p className="text-xs truncate" style={{ color: '#5A7A9B' }}>{user?.company}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="hover:bg-[#E7F0FA]"
                style={{ color: '#5A7A9B' }}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="w-full hover:bg-[#E7F0FA]"
              style={{ color: '#5A7A9B' }}
              data-testid="logout-button-collapsed"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header - Mobile */}
        <header className="md:hidden dashboard-header h-16 flex items-center justify-between px-4">
          <EMatrixLogo size="small" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            style={{ color: '#0D2440' }}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b p-4 space-y-2 animate-fade-in" style={{ borderColor: '#C8D8E8' }}>
            {navItems.filter(item => item.show).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <button
              onClick={handleLogout}
              className="sidebar-link w-full text-left"
            >
              <LogOut className="h-5 w-5" />
              <span>Abmelden</span>
            </button>
          </div>
        )}

        {/* Header - Desktop */}
        <header className="hidden md:flex dashboard-header h-16 items-center justify-between px-6">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: '#0D2440' }}>Service Portal</h2>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#E7F0FA]" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-sm" style={{ background: '#E7F0FA', color: '#2E5E99' }}>
                    {getInitials(user?.name || 'U')}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm" style={{ color: '#0D2440' }}>{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span style={{ color: '#0D2440' }}>{user?.name}</span>
                  <span className="text-xs font-normal" style={{ color: '#5A7A9B' }}>{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="mr-2 h-4 w-4" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          {children}
        </main>
      </div>

      {/* Data Upload Dialog */}
      <DataUploadDialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen} />

      {/* Helper Teams Configuration Dialog */}
      <HelperTeamsConfigDialog 
        open={helperTeamsDialogOpen} 
        onOpenChange={setHelperTeamsDialogOpen}
      />

      {/* Delete Data Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Datenstand entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den aktuellen Datenstand wirklich entfernen? 
              Alle geladenen Daten werden gelöscht und die Services sind nicht mehr verfügbar, 
              bis Sie einen neuen Datenstand laden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveData}
              className="bg-red-600 hover:bg-red-700"
              disabled={dataLoading}
            >
              {dataLoading ? 'Wird entfernt...' : 'Entfernen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Layout;
